import { CalendarDays } from 'lucide-react';
import { useEffect, useRef, useState, type ComponentProps } from 'react';
import { applyDateShortcut, displayDate, parseDisplayDate } from '@/lib/dates';
import { cn } from '@/lib/utils';

/**
 * DateInput (document 05): shows dd/MM/yyyy, accepts typed dates and the QBO shortcuts
 * (T today, +/- day, W/K week, M/H month, Y/R year); the calendar button opens the browser picker.
 */
export function DateInput({
  value,
  onChange,
  className,
  invalid,
  ...props
}: Omit<ComponentProps<'input'>, 'value' | 'onChange' | 'type'> & {
  value: string;
  onChange: (iso: string) => void;
  invalid?: boolean;
}) {
  const [text, setText] = useState(displayDate(value));
  const picker = useRef<HTMLInputElement>(null);
  useEffect(() => setText(displayDate(value)), [value]);

  return (
    <div className={cn('relative', className)}>
      <input
        {...props}
        type="text"
        inputMode="numeric"
        placeholder="dd/mm/yyyy"
        value={text}
        aria-invalid={invalid || undefined}
        onChange={(e) => setText(e.target.value)}
        onKeyDown={(e) => {
          if (e.ctrlKey || e.metaKey || e.altKey || e.key.length !== 1) return;
          const next = applyDateShortcut(e.key, value);
          if (next) {
            e.preventDefault();
            onChange(next);
          }
        }}
        onBlur={(e) => {
          const iso = parseDisplayDate(text);
          if (iso) onChange(iso);
          else setText(displayDate(value));
          props.onBlur?.(e);
        }}
        className="h-9 w-full rounded-md border border-input bg-card px-3 pr-9 text-sm shadow-xs outline-none focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 aria-invalid:border-destructive"
      />
      <button
        type="button"
        tabIndex={-1}
        aria-label="Open calendar"
        className="absolute right-1 top-1 rounded p-1.5 text-muted-foreground hover:bg-accent"
        onClick={() => picker.current?.showPicker?.()}
      >
        <CalendarDays className="size-4" />
      </button>
      <input
        ref={picker}
        type="date"
        tabIndex={-1}
        aria-hidden
        className="pointer-events-none absolute bottom-0 right-0 size-0 opacity-0"
        value={value}
        onChange={(e) => e.target.value && onChange(e.target.value)}
      />
    </div>
  );
}
